import type { VariantRow } from "@/modules/catalog/domain/types";
import { FormRoot, FormInput, FormPrice, RHF, zodResolver } from "@/shared/forms";
import { Button } from "@/components/ui/button";
import { Modal } from "@/shared/ui/Modal";


import { useEffect } from "react";
import { z } from "zod";

const VariantFormSchema = z.object({
  name: z.string().min(1, "ຕ້ອງໃສ່ຊື່ variant"),
  sku: z.string().nullable().optional(),
  barcode: z.string().nullable().optional(),
  basePriceLak: z.number().int().min(0, "ລາຄາຕ້ອງ ≥ 0"),
});

type VariantFormValues = z.infer<typeof VariantFormSchema>;

export type VariantFormPayload = {
  name: string;
  sku: string | null;
  barcode: string | null;
  basePriceLak: number;
};

type VariantFormModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant?: VariantRow | null;
  onSubmit: (payload: VariantFormPayload) => Promise<void>;
  submitting: boolean;
};

export function VariantFormModal({
  open,
  onOpenChange,
  variant,
  onSubmit,
  submitting,
}: VariantFormModalProps) {
  const isEdit = !!variant;

  const methods = RHF.useForm<VariantFormValues>({
    resolver: zodResolver(VariantFormSchema),
    defaultValues: {
      name: variant?.name ?? "",
      sku: variant?.sku ?? null,
      barcode: variant?.barcode ?? null,
      basePriceLak: variant?.basePriceLak ?? 0,
    },
  });

  useEffect(() => {
    if (open) {
      methods.reset({
        name: variant?.name ?? "",
        sku: variant?.sku ?? null,
        barcode: variant?.barcode ?? null,
        basePriceLak: variant?.basePriceLak ?? 0,
      });
    }
  }, [
    open,
    variant?.name,
    variant?.sku,
    variant?.barcode,
    variant?.basePriceLak,
    methods.reset,
  ]);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? "ແກ້ໄຂ variant" : "ເພີ່ມ variant"}
      description={isEdit ? "ແກ້ໄຂຂໍ້ມູນ variant" : "ຕື່ມຂໍ້ມູນ variant ໃໝ່"}
      size="sm"
    >
      <FormRoot<VariantFormValues>
        methods={methods}
        onSubmit={async (values) => {
          await onSubmit({
            name: values.name,
            sku: values.sku ?? null,
            barcode: values.barcode ?? null,
            basePriceLak: values.basePriceLak,
          });
          onOpenChange(false);
        }}
        className="space-y-4"
      >
        <FormInput
          name="name"
          label="ຊື່"
          placeholder="ຕົວຢ່າງ: M, 250g - Ground"
          requiredMark
        />
        <FormInput name="sku" label="SKU" placeholder="COF-ARB-250G" />
        <FormInput name="barcode" label="ບາໂຄດ" placeholder="885123456789" />
        <FormPrice
          name="basePriceLak"
          label="ລາຄາຕົ້ນ (LAK)"
          requiredMark
          currencySymbol="₭"
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            ຍົກເລີກ
          </Button>
          <Button type="submit" isLoading={submitting}>
            {isEdit ? "ບັນທຶກ" : "ເພີ່ມ"}
          </Button>
        </div>
      </FormRoot>
    </Modal>
  );
}
